/* eslint-disable no-param-reassign */
import React from 'react';
import ReactDOM from 'react-dom';
import useFlags from '@/hooks/useFlags';
import GulfOfMexicoNotice from './GulfOfMexicoNotice';

// AIDEV-NOTE: This initializer runs globally from application.js. It gates on the
// gulf_of_america_messaging_enabled LaunchDarkly flag (targeting is handled in LD,
// e.g. by subscriber state), then mounts the notice into one of two containers:
//   1. #present-navigator (slideshow — position:absolute, rides along with the auto-hiding toolbar)
//   2. <body> (default — position:fixed at viewport bottom, offset from #left_nav_bar)
// If the slideshow navigator mounts after init (Apollo/React bootstrap), watchForNavigator
// re-parents the banner into it.

const MOUNT_ID = 'gulf-of-mexico-notice-root';
const NOTICE_OFFSET_VAR = '--gulf-notice-offset';

// AIDEV-NOTE: WCAG 1.4.10 (PLAT-17316) — the body-mounted banner is position:fixed, so the
// page reserves no space for it; at 320px its wrapped text grows tall and overlaps the content
// scrolled underneath. Mirror the banner's live height into --gulf-notice-offset so
// _reflow_shared.scss can pad the page bottom by it. Only while it's the body-pinned banner —
// in the slideshow navigator it's position:absolute and rides the toolbar, so clear the offset
// whenever the banner isn't a direct child of <body>.
const syncNoticeOffset = (mountDiv: HTMLDivElement): void => {
  if (mountDiv.parentElement === document.body) {
    document.documentElement.style.setProperty(NOTICE_OFFSET_VAR, `${mountDiv.offsetHeight}px`);
  } else {
    document.documentElement.style.removeProperty(NOTICE_OFFSET_VAR);
  }
};

const createMountDiv = (): HTMLDivElement => {
  const div = document.createElement('div');
  div.id = MOUNT_ID;
  return div;
};

// AIDEV-NOTE: #present-navigator is position:fixed and moves with auto-hide (bottom: -50px).
// By mounting inside it with position:absolute + bottom:100%, the banner sits just above the
// toolbar and rides along automatically — no auto-hide tracking needed.
const applyNavigatorLayout = (mountDiv: HTMLDivElement): void => {
  mountDiv.style.position = 'absolute';
  mountDiv.style.bottom = '100%';
  mountDiv.style.left = '0';
  mountDiv.style.width = '100%';
  mountDiv.style.zIndex = '';
};

const mountInNavigator = (mountDiv: HTMLDivElement): boolean => {
  const navContent = document.getElementById('present-navigator');
  if (!navContent) return false;

  navContent.appendChild(mountDiv);
  applyNavigatorLayout(mountDiv);
  return true;
};

// AIDEV-NOTE: In drawer mode the nav is an off-canvas overlay, so the banner spans full width
// rather than offsetting from its right edge. Mirrors the SCSS drawer condition.
const DRAWER_BREAKPOINT = '(max-width: 68.75rem)';

const isDrawerMode = (): boolean => {
  const { classList } = document.body;
  const reflowPage = classList.contains('reflow-enabled')
    && (classList.contains('reflow-page') || classList.contains('full-width-layout'));
  return reflowPage && window.matchMedia?.(DRAWER_BREAKPOINT).matches === true;
};

// AIDEV-NOTE: Banner is position:fixed so it's always pinned to the viewport bottom
// regardless of page/content height. Left offset is derived from #left_nav_bar's right
// edge (the nav is position:fixed) so the banner never slides under the nav, and we
// re-sync on window resize, nav collapse (body class toggle), and nav late-mount.
const syncBannerLayout = (mountDiv: HTMLDivElement): void => {
  if (isDrawerMode()) {
    mountDiv.style.left = '0';
    mountDiv.style.width = '100%';
    return;
  }
  const leftNav = document.getElementById('left_nav_bar');
  const leftOffset = leftNav ? leftNav.getBoundingClientRect().right : 0;
  mountDiv.style.left = `${leftOffset}px`;
  mountDiv.style.width = `${window.innerWidth - leftOffset}px`;
};

// AIDEV-NOTE: Returns the WCAG 1.4.10 ResizeObserver (or null when the reflow flag is off) so
// the caller can disconnect it if the banner later moves into the slideshow navigator.
const mountInBody = (mountDiv: HTMLDivElement, accessibilityReflow?: boolean): ResizeObserver | null => {
  document.body.appendChild(mountDiv);
  mountDiv.style.position = 'fixed';
  mountDiv.style.bottom = '0';
  mountDiv.style.zIndex = '9999';
  syncBannerLayout(mountDiv);

  // AIDEV-NOTE: Only watch body class changes (nav collapse toggles body.left-nav-collapsed).
  // The nav itself is server-rendered so it's present at DOM ready — no need to observe subtree.
  let debounceId: ReturnType<typeof setTimeout> | null = null;
  const handleResize = () => {
    if (debounceId) clearTimeout(debounceId);
    debounceId = setTimeout(() => syncBannerLayout(mountDiv), 50);
  };
  window.addEventListener('resize', handleResize);
  const observer = new MutationObserver(handleResize);
  observer.observe(document.body, { attributeFilter: ['class'], attributes: true });

  // AIDEV-NOTE: WCAG 1.4.10 (PLAT-17316) — gated on accessibility_reflow so flag-off
  // rendering is byte-identical. ResizeObserver (not the resize listener) so browser
  // font-size zoom — which grows the wrapped banner without a window resize — is also
  // caught. React renders null on dismiss → height 0 → offset resolves to 0.
  if (!accessibilityReflow) return null;

  const offsetObserver = new ResizeObserver(() => syncNoticeOffset(mountDiv));
  offsetObserver.observe(mountDiv);
  return offsetObserver;
};

const mount = (mountDiv: HTMLDivElement, accessibilityReflow?: boolean): ResizeObserver | null => {
  const offsetObserver = mountInNavigator(mountDiv)
    ? null
    : mountInBody(mountDiv, accessibilityReflow);

  ReactDOM.render(React.createElement(GulfOfMexicoNotice), mountDiv);
  return offsetObserver;
};

// AIDEV-NOTE: Watches for #present-navigator to appear asynchronously (React/Apollo mount).
// When found, re-parents the mount div inside it and switches to navigator layout.
const watchForNavigator = (mountDiv: HTMLDivElement, offsetObserver: ResizeObserver | null): void => {
  const observer = new MutationObserver(() => {
    const navContent = document.getElementById('present-navigator');
    if (navContent && mountDiv.parentElement !== navContent) {
      navContent.appendChild(mountDiv);
      applyNavigatorLayout(mountDiv);
      syncNoticeOffset(mountDiv); // no longer body-pinned — release any reserved page-bottom space
      offsetObserver?.disconnect(); // banner left <body> — stop tracking its height
      observer.disconnect();
    }
  });

  observer.observe(document.body, { childList: true, subtree: true });
};

const init = (): void => {
  // AIDEV-NOTE: Read the flags once and thread them down so the DOM lookup
  // ($('#slug-attributes').data) happens a single time per init.
  const { accessibilityReflow, gulfOfAmericaMessagingEnabled } = useFlags();
  if (!gulfOfAmericaMessagingEnabled) return;

  // AIDEV-NOTE: Suppress in same-origin iframes (e.g. slideshow reading sections) where
  // the parent TCI page already shows the banner. Allow in cross-origin iframes (e.g. LMS
  // embeds via LTI) where there's no parent TCI page to show it.
  if (window !== window.top) {
    try {
      if (window.parent.document) return; // same-origin parent — suppress
    } catch {
      // cross-origin parent (LMS) — proceed and show banner
    }
  }

  const existing = document.getElementById(MOUNT_ID);
  if (existing) return;

  const mountDiv = createMountDiv();
  const offsetObserver = mount(mountDiv, accessibilityReflow);

  // If Navigator wasn't found initially, watch for it to appear
  if (!document.getElementById('present-navigator')) {
    watchForNavigator(mountDiv, offsetObserver);
  }
};

export { init, MOUNT_ID };
